import { useEffect, useMemo, useState } from 'react';
import {
    HeaderGroup,
    useTable,
    Cell,
    Column,
    usePagination,
    useFilters,
    useGlobalFilter,
    useAsyncDebounce,
    useExpanded,
} from 'react-table';
import {
    Grid,
    OutlinedInput,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { EmptyTable, HidingSelect, TablePagination } from 'components/third-party/ReactTable';
import { LabelKeyObject } from 'react-csv/components/CommonPropTypes';
import { DefaultColumnFilter } from 'utils/react-table';
import { CaretUpOutlined, CaretDownOutlined, SearchOutlined } from '@ant-design/icons';
import BaseTableProps, { SortingType } from './dto';
import { TableLoadingSkeleton } from './table-loading-skeleton';

const BaseTable = <T extends {}>({
    data,
    fetchData,
    pageCount: controlledPageCount,
    defaultColumnConfig,
    defaultVisibleColumnConfig,
    defaultColumnIdToSortBy,
    defaultSortingType = SortingType.Descending,
    previewComponent,
    defaultFilters = [],
    hideSearch = false,
    hideColumnSelector = false,
    hidePagination = false,
    onRowClick,
    defaultSearchTerm = '',
    defaultPageSize = 10,
    defaultPageIndex = 0,
    onColumnsVisibilityChange,
}: BaseTableProps<T>) => {
    const defaultColumn = useMemo(() => ({ Filter: DefaultColumnFilter }), []);
    const [searchTerm, setSearchTerm] = useState<string>(defaultSearchTerm);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [sortingType, setSortingType] = useState<SortingType>(defaultSortingType);
    const [sortBy, setSortBy] = useState<string>(defaultColumnIdToSortBy ?? 'id');

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        setHiddenColumns,
        visibleColumns,
        allColumns,
        gotoPage,
        setPageSize,
        pageCount,
        setGlobalFilter: _setSearchTerm,
        state: { hiddenColumns, pageIndex, pageSize, filters, globalFilter: _searchTerm },
    } = useTable(
        {
            columns: defaultColumnConfig,
            data,
            initialState: {
                pageIndex: defaultPageIndex,
                pageSize: defaultPageSize,
                hiddenColumns: defaultColumnConfig
                    .filter((col: Column<{}>) => !defaultVisibleColumnConfig?.includes(col.accessor as string))
                    .map((col) => col.accessor) as string[],
                filters: defaultFilters ?? [],
                globalFilter: defaultSearchTerm ?? '',
            },
            pageCount: controlledPageCount,
            manualPagination: true,
            defaultColumn,
            manualFilters: true,
            manualSorting: true,
            manualGlobalFilter: true,
        },
        useFilters,
        useGlobalFilter,
        useExpanded,
        usePagination,
    );

    let headers: LabelKeyObject[] = [];
    allColumns.map((item) => {
        if (!hiddenColumns?.includes(item.id)) {
            headers.push({ label: item.Header as string, key: item.id });
        }
        return item;
    });

    const fetchDataDebounce = useAsyncDebounce(({ pageIndex, pageSize, sortingType, sortBy, filters, searchTerm }) => {
        setIsLoading(true);
        fetchData({ pageIndex, pageSize, sortingType, sortBy, filters, searchTerm }).finally(() => setIsLoading(false));
    }, 500);

    const getSortedDataOnClick = (isSortable: boolean | undefined, columnId: string) => {
        if (isSortable) {
            const columnIdParts = columnId.split('.');
            const columnName = columnIdParts[columnIdParts.length - 1]; //for id - id | for localizedContents.en.shortName - shortName
            if (columnName === sortBy) {
                sortingType === defaultSortingType
                    ? reverseSortingType(defaultSortingType)
                    : setSortingParameters(defaultSortingType, defaultColumnIdToSortBy ?? 'id');
            } else {
                setSortingParameters(defaultSortingType, columnName);
            }
        }
    };

    const setSortingParameters = (sortingType: SortingType, sortBy: string) => {
        setSortingType(sortingType);
        setSortBy(sortBy);
    };

    const reverseSortingType = (sortingType: string) => {
        if (sortingType === SortingType.Ascending) setSortingType(SortingType.Descending);
        else setSortingType(SortingType.Ascending);
    };

    useEffect(() => {
        fetchDataDebounce({ pageIndex, pageSize, sortingType, sortBy, filters, searchTerm });
    }, [fetchDataDebounce, pageIndex, pageSize, sortingType, sortBy, filters, searchTerm]);

    return (
        <Stack spacing={2}>
            {(!hideSearch || !hideColumnSelector) && (
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="start"
                    sx={{ p: 2, pb: 0 }}
                >
                    {!hideSearch && (
                        <Stack spacing={1}>
                            <OutlinedInput
                                value={_searchTerm ?? ''}
                                onChange={(e) => {
                                    _setSearchTerm(e.target.value);
                                    if (e.target.value.length >= 3) {
                                        setSearchTerm(e.target.value);
                                    } else {
                                        setSearchTerm('');
                                    }
                                }}
                                id="user-table-search"
                                placeholder="Search..."
                                startAdornment={<SearchOutlined />}
                                size="small"
                            />
                            <Typography variant="caption" color="textSecondary">
                                Enter 3 or more characters to search
                            </Typography>
                        </Stack>
                    )}
                    {!hideColumnSelector && (
                        <HidingSelect
                            hiddenColumns={hiddenColumns!}
                            setHiddenColumns={(newHiddenColumns) => {
                                setHiddenColumns(newHiddenColumns);
                                onColumnsVisibilityChange?.(
                                    defaultColumnConfig
                                        .filter((column) => !newHiddenColumns.includes(column.accessor as string))
                                        .map((col) => col.accessor) as string[],
                                );
                            }}
                            allColumns={allColumns}
                        />
                    )}
                </Stack>
            )}
            <Table {...getTableProps()}>
                <TableHead sx={{ borderTopWidth: 2 }}>
                    {headerGroups.map((headerGroup) => (
                        <TableRow {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column: HeaderGroup) => (
                                <TableCell
                                    onClick={() => getSortedDataOnClick(!column.disableSortBy, column.id)}
                                    {...column.getHeaderProps([{ className: column.className }])}
                                >
                                    <Grid display="flex" justifyContent="center">
                                        {column.render('Header')}
                                        {column.disableSortBy === false && (
                                            <Stack paddingLeft="10px" direction="column">
                                                <CaretUpOutlined
                                                    style={{
                                                        width: '12px',
                                                        fontSize: '0.75rem',
                                                        color:
                                                            sortingType === SortingType.Ascending &&
                                                            column.id.split('.').at(-1) === sortBy
                                                                ? 'black'
                                                                : 'grey',
                                                    }}
                                                />
                                                <CaretDownOutlined
                                                    style={{
                                                        width: '12px',
                                                        fontSize: '0.75rem',
                                                        color:
                                                            sortingType !== SortingType.Ascending &&
                                                            column.id.split('.').at(-1) === sortBy
                                                                ? 'black'
                                                                : 'grey',
                                                    }}
                                                />
                                            </Stack>
                                        )}
                                    </Grid>
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableHead>
                <TableBody {...getTableBodyProps()}>
                    {headerGroups.map((group: HeaderGroup<{}>) => (
                        <TableRow {...group.getHeaderGroupProps()}>
                            {group.headers.map((column: HeaderGroup) => (
                                <TableCell {...column.getHeaderProps([{ className: column.className }])}>
                                    {column.canFilter ? column.render('Filter') : null}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                    {isLoading && <TableLoadingSkeleton rows={pageSize} columns={visibleColumns.length} />}
                    {!isLoading &&
                        (rows.length > 0 ? (
                            rows.map((row) => {
                                prepareRow(row);
                                return (
                                    <>
                                        <TableRow
                                            {...row.getRowProps()}
                                            onClick={() => onRowClick?.(row.original)}
                                            sx={
                                                onRowClick && {
                                                    cursor: 'pointer',
                                                    '&:hover': { bgcolor: 'action.hover' },
                                                }
                                            }
                                        >
                                            {row.cells.map((cell: Cell) => (
                                                <TableCell
                                                    {...cell.getCellProps([{ className: cell.column.className }])}
                                                >
                                                    {cell.render('Cell')}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                        {row.isExpanded ? (
                                            <tr>
                                                <td colSpan={visibleColumns.length}>
                                                    {previewComponent && previewComponent({ row })}
                                                </td>
                                            </tr>
                                        ) : null}
                                    </>
                                );
                            })
                        ) : (
                            <EmptyTable msg="No Data" colSpan={visibleColumns.length} />
                        ))}

                    {!hidePagination && (
                        <TableRow>
                            <TableCell sx={{ p: 2 }} colSpan={visibleColumns.length}>
                                <TablePagination
                                    gotoPage={gotoPage}
                                    rows={rows}
                                    setPageSize={setPageSize}
                                    pageIndex={pageIndex}
                                    pageSize={pageSize}
                                    pageCount={pageCount}
                                />
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Stack>
    );
};

export default BaseTable;
